/**
 * Column resize hook - handles mouse interactions for resizing table columns
 */

import { useCallback, useRef, useState } from 'react';

interface UseColumnResizeProps {
  onResize: (width: number) => void;
  initialWidth: number;
  minWidth?: number;
}

interface UseColumnResizeReturn {
  /** Current width being dragged (null if not dragging) */
  dragWidth: number | null;
  /** Props for the resize handle element */
  handleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
  };
}

/**
 * Hook to manage column resizing functionality
 *
 * Provides mouse event handlers and state for resizing table columns.
 * Shows visual feedback during drag and applies final width on mouse up.
 */
export function useColumnResize({
  onResize,
  initialWidth,
  minWidth = 80,
}: UseColumnResizeProps): UseColumnResizeReturn {
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      startXRef.current = e.clientX;
      startWidthRef.current = initialWidth;
      setDragWidth(initialWidth);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = Math.max(minWidth, startWidthRef.current + delta);
        setDragWidth(newWidth);
      };

      const handleMouseUp = () => {
        if (dragWidth !== null) {
          onResize(dragWidth);
        }
        setDragWidth(null);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [initialWidth, minWidth, onResize, dragWidth]
  );

  return {
    dragWidth,
    handleProps: {
      onMouseDown: handleMouseDown,
    },
  };
}
